/**
 * BlackboardWidget -- Dashboard-Widget fuer aktuelle Mitteilungen
 * ================================================================
 * News-Style Cards mit Layout-Support (Bild links/rechts/oben/text only).
 * Angepinnte Beitraege erscheinen oben mit Hervorhebung.
 * Wird in FirmaOverview als Widget eingebettet.
 */
import { useState, useEffect } from 'react'
import DOMPurify from 'dompurify'
import type { BlackboardPost, BlackboardAttachment } from '@/types'

// ── Hilfsfunktionen ──────────────────────────────────────────

function fmtDate(iso: string | null): string {
  if (!iso) return ''
  try {
    return new Date(iso).toLocaleDateString('de-DE', {
      day: '2-digit', month: '2-digit', year: 'numeric',
    })
  } catch {
    return iso
  }
}

function fmtFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function isImage(att: BlackboardAttachment): boolean {
  return att.attachment_type === 'image'
}

function pickHero(post: BlackboardPost): BlackboardAttachment | undefined {
  if (post.hero_attachment_id) {
    const explicit = post.attachments.find(a => a.id === post.hero_attachment_id && isImage(a))
    if (explicit) return explicit
  }
  return post.attachments.find(isImage)
}

function fileUrl(postId: number, filename: string): string {
  return `/api/blackboard/files/${postId}/${filename}`
}

interface BlackboardWidgetProps {
  accountId?: string
}

// ── Skeleton ─────────────────────────────────────────────────

function Skeleton() {
  return (
    <div className="space-y-4 animate-pulse">
      {[1, 2, 3].map(i => (
        <div key={i} className="p-4 rounded-lg bg-gray-50">
          <div className="h-4 bg-gray-200 rounded w-3/4 mb-2" />
          <div className="h-3 bg-gray-200 rounded w-full mb-1" />
          <div className="h-3 bg-gray-200 rounded w-5/6 mb-1" />
          <div className="h-3 bg-gray-200 rounded w-2/3" />
        </div>
      ))}
    </div>
  )
}

// ── Hauptkomponente ──────────────────────────────────────────

export default function BlackboardWidget({ accountId }: BlackboardWidgetProps) {
  const [posts, setPosts] = useState<BlackboardPost[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!accountId) {
      setLoading(false)
      return
    }
    let cancelled = false
    async function load() {
      try {
        const res = await fetch(
          `/api/firma/${accountId}/blackboard/posts?pageSize=10`,
          { credentials: 'include' },
        )
        const json = await res.json()
        if (!cancelled) {
          if (json.success && Array.isArray(json.data)) {
            setPosts(json.data)
          } else {
            setPosts([])
          }
        }
      } catch (e: any) {
        if (!cancelled) setError(e.message || 'Fehler beim Laden')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => { cancelled = true }
  }, [accountId])

  if (loading) return <Skeleton />

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-8 text-gray-400">
        <svg className="w-10 h-10 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v3m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <span className="text-sm">{error}</span>
      </div>
    )
  }

  if (posts.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-10 text-gray-400">
        <svg className="w-12 h-12 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v12a2 2 0 01-2 2zM12 10v4m-2-2h4" />
        </svg>
        <span className="text-sm font-medium">Keine Neuigkeiten</span>
        <span className="text-xs mt-1">Es gibt derzeit keine aktuellen Mitteilungen.</span>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {posts.map(post => <NewsCard key={post.id} post={post} />)}
    </div>
  )
}

// ── News-Card ────────────────────────────────────────────────

function NewsCard({ post }: { post: BlackboardPost }) {
  const hero = pickHero(post)
  const layout = post.layout || 'text'
  const docs = post.attachments.filter(a => !isImage(a))

  const heroEl = hero && (
    <img
      src={fileUrl(post.id, hero.filename)}
      alt=""
      className="w-full h-full object-cover"
    />
  )

  const headerLine = (
    <div className="flex items-center gap-2 mb-2 flex-wrap">
      {post.category === 'info' ? (
        <span className="inline-flex items-center px-2 py-0.5 bg-blue-50 text-blue-700 text-[10px] font-bold rounded-full uppercase tracking-wide">Info</span>
      ) : (
        <span className="inline-flex items-center px-2 py-0.5 bg-fom-50 text-fom-700 text-[10px] font-bold rounded-full uppercase tracking-wide">News</span>
      )}
      {post.is_pinned && (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-50 text-amber-700 text-[10px] font-bold rounded-full uppercase tracking-wide">
          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
            <path d="M9.828 3.414a2 2 0 012.829 0l1.929 1.929a2 2 0 010 2.828l-4.243 4.243a1 1 0 01-.707.293H7.1a1 1 0 01-.707-.293L4.464 10.485a2 2 0 010-2.829L9.828 3.414z" />
          </svg>
          Angepinnt
        </span>
      )}
      <span className="text-[11px] text-gray-400">
        {fmtDate(post.created_at)} &middot; {post.author_name}
      </span>
    </div>
  )

  const titleEl = (
    <h3 className="text-base font-bold text-gray-900 leading-tight mb-2">
      {post.title}
    </h3>
  )

  const contentEl = (
    <div
      className="text-sm text-gray-600 leading-relaxed prose-newscard"
      dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(post.content) }}
    />
  )

  const docsEl = docs.length > 0 && (
    <div className="flex flex-wrap gap-2 mt-3">
      {docs.map(att => (
        <a
          key={att.id}
          href={fileUrl(post.id, att.filename)}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-md text-xs text-gray-600 hover:text-gray-800 transition-colors"
        >
          <svg className="w-3.5 h-3.5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3M3 17V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
          </svg>
          <span className="max-w-[140px] truncate">{att.original_name}</span>
          <span className="text-gray-400">({fmtFileSize(att.file_size)})</span>
        </a>
      ))}
    </div>
  )

  const cardOuter = `bg-white rounded-xl border overflow-hidden ${
    post.is_pinned ? 'border-fom-200 ring-2 ring-fom-50' : 'border-gray-200'
  }`

  // ── Layouts ────────────────────────────────────────────────
  if (layout === 'image-top' && hero) {
    return (
      <article className={cardOuter}>
        <div className="w-full aspect-[16/7] overflow-hidden">{heroEl}</div>
        <div className="p-5">
          {headerLine}{titleEl}{contentEl}{docsEl}
        </div>
      </article>
    )
  }

  if ((layout === 'image-left' || layout === 'image-right') && hero) {
    const img = (
      <div className="flex-shrink-0 w-2/5 min-h-[160px] overflow-hidden">{heroEl}</div>
    )
    const text = (
      <div className="flex-1 p-5 min-w-0">
        {headerLine}{titleEl}{contentEl}{docsEl}
      </div>
    )
    return (
      <article className={cardOuter}>
        <div className="flex items-stretch">
          {layout === 'image-left' ? <>{img}{text}</> : <>{text}{img}</>}
        </div>
      </article>
    )
  }

  // text only / kein Hero
  return (
    <article className={cardOuter}>
      <div className="p-5">
        {headerLine}{titleEl}{contentEl}{docsEl}
      </div>
    </article>
  )
}
